/**
 * MobileOptimizer — the mobile-native Roster Optimizer surface (DD-MOBILE-OPTIMIZER-0724).
 *
 * §14.0: mobile-native, shares only the backend (getOptimizerGaps / getOptimizerTrades). No
 * desktop-component import. Progressive load — /gaps paints the header + gap strip in ~1s
 * while /trades streams the recommendations. Flag-OFF is gated on the RESPONSE shape (not a
 * client flag read): if /trades lacks the new-shape markers the screen degrades loudly rather
 * than rendering old-shape data as new (Phase 1 decision).
 *
 * Frames: 1 main feed · 2 progressive loading · 6 degraded/empty (here); 3 all-trades + B
 * risk-grouped (MobileOptimizerAllTrades); 4 trade detail + A three-team (MobileTradeSheet);
 * 5 FA swap (MobileFASwapSheet).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { leaguesAPI } from '../../../../services/apiService';
import {
  C, SP, FS, R, PRIMARY, BORDER, BORDER_SOFT, NUM, withAlpha,
  tierColor, sectionHeaderStyle, pillStyle,
} from './optimizerTokens';
import MobileOptimizerAllTrades from './MobileOptimizerAllTrades';
import MobileTradeSheet from './MobileTradeSheet';
import MobileFASwapSheet from './MobileFASwapSheet';
import MobileFieldView from './MobileFieldView';

// DD-RO-MOBILE-FIELD-FIX-0728 #4: abbreviated labels so the mode group stays compact in the single tight
// controls row (Bal/Win/Dyn), matching the approved mockup.
const MODES = [['balanced', 'Bal'], ['win_now', 'Win'], ['dynasty', 'Dyn']];
// DD-RO-MOBILE-FIELD-0728: the primary view switch. Field = the pinned-diamond player-tap swap surface.
const VIEWS = [['field', 'Field'], ['feed', 'Gaps'], ['all', 'Trades']];
// DD-RO-MOBILE-TOPBAR-0728: single-letter H/P — the full words pushed the row to a second line.
const SIDES = [['hitters', 'H'], ['pitchers', 'P']];

/**
 * Compact segmented control — the mobile equivalent of the desktop 28px segmented primitive
 * (DD-RO-MOBILE-FIELD-FIX-0728 #4). One tight group of joined cells, gold active with dark text; sits
 * inline in the controls row rather than as a full-width stacked block.
 */
function Seg({ options, value, onChange, label, testid }) {
  return (
    <div style={{ display: 'inline-flex', border: BORDER, borderRadius: R.none, overflow: 'hidden', flexShrink: 0 }}
         role="tablist" aria-label={label}>
      {options.map(([id, txt], i) => (
        <button key={id} type="button" role="tab" aria-selected={value === id}
          data-testid={testid ? `${testid}-${id}` : undefined}
          onClick={() => onChange(id)}
          style={{
            padding: '4px 6px', fontSize: FS['2xs'], lineHeight: 1, whiteSpace: 'nowrap',
            fontWeight: value === id ? 800 : 700, cursor: 'pointer',
            border: 'none', borderLeft: i > 0 ? BORDER : 'none', borderRadius: R.none,
            color: value === id ? C.neutral[950] : C.neutral[400],
            background: value === id ? PRIMARY : withAlpha(C.neutral[900], 0.6), ...NUM,
          }}>{txt}</button>
      ))}
    </div>
  );
}

/**
 * Text tabs — the primary view switch (Field / Gaps / Trades) as PLAIN TEXT with an underline on the active
 * one (DD-RO-MOBILE-TOPBAR-0728, screenshot 1), NOT boxed segment pills. Keeps role="tab" + the view-<id>
 * testids so behavior/telemetry are unchanged.
 */
function TextTabs({ options, value, onChange, label, testid }) {
  return (
    <div style={{ display: 'inline-flex', gap: SP.md, alignItems: 'center' }} role="tablist" aria-label={label}>
      {options.map(([id, txt]) => {
        const on = value === id;
        return (
          <button key={id} type="button" role="tab" aria-selected={on}
            data-testid={testid ? `${testid}-${id}` : undefined}
            onClick={() => onChange(id)}
            style={{
              padding: '2px 0', fontSize: FS.sm, lineHeight: 1, whiteSpace: 'nowrap', cursor: 'pointer',
              fontWeight: on ? 800 : 700, background: 'none', border: 'none',
              borderBottom: on ? `2px solid ${PRIMARY}` : '2px solid transparent',
              color: on ? PRIMARY : C.neutral[400], ...NUM,
            }}>{txt}</button>
        );
      })}
    </div>
  );
}

/** The new-shape contract the screen requires (Phase 1). Absent → flag-OFF path → degrade. */
export function tradesAreNewShape(trades) {
  if (!trades) return false;
  const hasCoverage = !!(trades.meta && trades.meta.projection_coverage);
  const recs = trades.recommendations || [];
  // A server-authoritative FA swap carries `drop`; old-shape recs are bare adds.
  const hasServerDrop = recs.length === 0 || recs.some((r) => r && r.drop);
  return hasCoverage && hasServerDrop;
}

function SectionHeader({ label, color, right }) {
  const s = sectionHeaderStyle(color);
  return (
    <div style={s.row}>
      <span style={s.bar} /><span style={s.label}>{label}</span>
      {right != null && <span style={{ marginLeft: 'auto', fontSize: FS['2xs'], color: C.neutral[500], ...NUM }}>{right}</span>}
    </div>
  );
}

export default function MobileOptimizer({ leagueId, teamId, onOpenTradeCenter, onPlayerClick }) {
  const [mode, setMode] = useState('balanced');
  const [gaps, setGaps] = useState(null);
  const [trades, setTrades] = useState(null);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [error, setError] = useState(null);
  // DD-RO-MOBILE-FIELD-FIX-0728 #1: Field is the primary landing view — it loads per-player on tap and
  // NEVER triggers the team-level optimization, so the screen no longer 504s on mount.
  const [view, setView] = useState('field');           // 'field' | 'feed' (gaps) | 'all' (trades)
  const [side, setSide] = useState('hitters');         // DD-RO-MOBILE-FIELD-0728: field-view side toggle
  // DD-RO-BUILD-3-AND-4-0729 Item 4: tapping a Category Gaps box filters the moves to that category
  // (desktop parity — the same fills_needs.categories logic OptimizerTradeRecs uses). Tap again to clear.
  const [catFilter, setCatFilter] = useState(null);
  const toggleCat = useCallback((c) => setCatFilter((prev) => (prev === c ? null : c)), []);
  const [tradesRequested, setTradesRequested] = useState(false);  // #1: team trades load lazily, on demand
  const [sheet, setSheet] = useState(null);            // { kind:'trade'|'fa', data }

  // FAST: gaps + standings + header (no mode dependence).
  const fetchGaps = useCallback(() => {
    if (!leagueId || !teamId) return Promise.resolve();
    return leaguesAPI.getOptimizerGaps(leagueId, teamId)
      .then((d) => setGaps(d))
      .catch((e) => setError(e?.message || 'gap load failed'));
  }, [leagueId, teamId]);

  // DD-RO-UI-0727 #5: the gap-strip RANK must be the AUTHORITATIVE current standings (calculate_rotisserie_
  // standings via getTeamCategoryStandings basis='current'), not the projected category_gaps rank the backend
  // fills on basis='projected' — the same defect the desktop sidebar had (SO='111 behind' shown as current).
  const [authStandings, setAuthStandings] = useState(null);
  useEffect(() => {
    if (!leagueId || !teamId) return;
    let alive = true;
    leaguesAPI.getTeamCategoryStandings(leagueId, teamId, 'current', null)
      .then((r) => { if (alive) setAuthStandings(r); })
      .catch(() => { if (alive) setAuthStandings(null); });
    return () => { alive = false; };
  }, [leagueId, teamId]);

  // SLOW: trades (re-fetched on mode change — mode moves output on 10/10 teams).
  const fetchTrades = useCallback(() => {
    if (!leagueId || !teamId) return Promise.resolve();
    setTradesLoading(true);
    return leaguesAPI.getOptimizerTrades(leagueId, teamId, mode)
      .then((d) => { setTrades(d); setTradesLoading(false); })
      .catch((e) => { setError(e?.message || 'trade load failed'); setTradesLoading(false); });
  }, [leagueId, teamId, mode]);

  // DD-RO-MOBILE-FIELD-FIX-0728 #1: mark team trades as needed the FIRST time a view that consumes them
  // (Gaps feed / Trades) is opened. Until then the heavy /optimizer/trades call never fires.
  useEffect(() => {
    if (view === 'feed' || view === 'all') setTradesRequested(true);
  }, [view]);

  useEffect(() => { fetchGaps(); }, [fetchGaps]);
  // #1: the team-level optimization (/optimizer/trades — the call that 504s) fires ONLY once the user
  // opens Gaps/Trades, never on mount and never for the Field view.
  useEffect(() => { if (tradesRequested) fetchTrades(); }, [tradesRequested, fetchTrades]);

  // DD-MOBILE-OPTIMIZER-ACTIONS-0724: after a mutation from this screen (a confirmed swap),
  // re-fetch so recommendations reflect the NEW roster, never the pre-swap one — this screen's
  // guard against a stale optimizer result (§4.5.10.2, the Ty-France class). Team trades are only
  // re-fetched if they were already loaded — a field-view swap never spins up the team optimization.
  const refetch = useCallback(
    () => Promise.all([fetchGaps(), tradesRequested ? fetchTrades() : Promise.resolve()]),
    [fetchGaps, fetchTrades, tradesRequested],
  );

  const openSheet = useCallback((kind, data) => setSheet({ kind, data }), []);
  const closeSheet = useCallback(() => setSheet(null), []);

  // ---- flag-OFF gate: trades loaded but NOT new-shape → degrade explicitly ----
  const tradesLoaded = !tradesLoading && trades != null;
  const engineUpgrading = tradesLoaded && !tradesAreNewShape(trades);

  return (
    <div style={{ padding: `0 ${SP.sm} ${SP.lg}`, ...NUM }} data-testid="mobile-optimizer">
      {/* DD-RO-MOBILE-TOPBAR-0728: the mockup's SINGLE compact top row — Field/Gaps/Trades + Hitters/Pitchers
          + Bal/Win/Dyn, all on one line (no wrap). The team-name/standings banner is GONE (not in the mockup). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SP.xs,
                    flexWrap: 'nowrap', margin: `${SP.sm} 0 ${SP.xs}` }} data-testid="optimizer-topbar">
        <TextTabs options={VIEWS} value={view} onChange={setView} label="Optimizer view" testid="view" />
        {view === 'field' && (
          <Seg options={SIDES} value={side} onChange={setSide} label="Field side" testid="side" />
        )}
        <Seg options={MODES} value={mode} onChange={setMode} label="Optimizer mode" testid="mode" />
      </div>

      {error && (
        <div style={{ padding: SP.md, color: C.error, fontSize: FS.sm }} data-testid="optimizer-error">
          Couldn’t load the optimizer. {String(error)}
        </div>
      )}

      {/* FRAME 6 (flag-OFF degrade): explicit, visible — never render old-shape as new */}
      {engineUpgrading && (
        <div style={{ padding: `${SP.lg} ${SP.sm}`, textAlign: 'center', borderTop: BORDER }} data-testid="optimizer-engine-upgrading">
          <div style={{ fontSize: FS.lg, fontWeight: 700, color: C.warning }}>Optimizer engine is upgrading</div>
          <div style={{ fontSize: FS.sm, color: C.neutral[400], marginTop: SP.xs, lineHeight: 1.5 }}>
            Trade recommendations are temporarily unavailable on mobile. Gaps below are current.
          </div>
        </div>
      )}

      {view === 'field' ? (
        // DD-RO-MOBILE-FIELD-0728: onRequestSwap omitted → the component's own fallback opens MobileFASwapSheet
        // with {player, drop} via the canonical generatePlanForDrop/executePlan (no new mutation). onComplete=
        // refetch is the §4.5.10.2 stale-result guard. sequences feeds the desktop-parity cutSeqs merge.
        <MobileFieldView
          leagueId={leagueId} teamId={teamId} mode={mode} side={side}
          meta={trades?.meta || gaps?.meta}
          onPlayerClick={onPlayerClick} onComplete={refetch}
        />
      ) : view === 'all' && !engineUpgrading ? (
        <MobileOptimizerAllTrades
          gaps={gaps} trades={trades} teamId={teamId}
          onBack={() => setView('feed')} onOpen={openSheet}
        />
      ) : (
        <MainFeed
          gaps={gaps} trades={trades} tradesLoading={tradesLoading}
          engineUpgrading={engineUpgrading} teamId={teamId} authStandings={authStandings}
          onSeeAll={() => setView('all')} onOpen={openSheet}
          catFilter={catFilter} onGapTap={toggleCat}
        />
      )}

      {sheet?.kind === 'trade' && (
        <MobileTradeSheet sequence={sheet.data} teamId={teamId} onClose={closeSheet}
          onOpenTradeCenter={onOpenTradeCenter} onPlayerClick={onPlayerClick} />
      )}
      {sheet?.kind === 'fa' && (
        <MobileFASwapSheet rec={sheet.data} leagueId={leagueId} onClose={closeSheet}
          onComplete={refetch} onPlayerClick={onPlayerClick} />
      )}
    </div>
  );
}

/** FRAME 1 (main feed) + FRAME 2 (progressive loading for the trades section). */
function MainFeed({ gaps, trades, tradesLoading, engineUpgrading, teamId, authStandings, onSeeAll, onOpen, catFilter, onGapTap }) {
  // DD-RO-BUGS2-0724 Bug 1: /gaps paints an instant approximation (thinner FA pool); /trades
  // carries the AUTHORITATIVE category_gaps /full computed. Prefer the authoritative set once it
  // lands so the mobile strip converges to desktop /full instead of diverging on max_gainable.
  const catGaps = trades?.category_gaps || gaps?.category_gaps || [];
  const proj = trades?.projected_standings || {};
  const gainable = proj.optimized && proj.current
    ? Math.round((proj.optimized.your_points - proj.current.your_points) * 10) / 10 : null;
  const allRecs = trades?.recommendations || [];
  const allSequences = (trades?.trade_engine?.trade_sequences || []);
  const coverage = trades?.meta?.projection_coverage;
  // DD-RO-BUILD-3-AND-4-0729 Item 4: filter moves to the tapped category. A trade addresses a category
  // via the SAME field the desktop uses (pkg.fills_needs.categories, OptimizerTradeRecs); an FA rec via a
  // positive per-category delta in its score.
  const seqAddresses = (s, c) => ((s.trade_package || s.trade || {}).fills_needs?.categories || []).includes(c);
  const recAddresses = (r, c) => (r.score?.category_deltas || []).some(
    (d) => d.category === c && ((d.pts_delta ?? d.points_delta ?? d.stat_delta ?? 0) > 0));
  const recs = catFilter ? allRecs.filter((r) => recAddresses(r, catFilter)) : allRecs;
  const sequences = catFilter ? allSequences.filter((s) => seqAddresses(s, catFilter)) : allSequences;

  return (
    <div>
      {/* headline gainable pts (from /trades; shows a skeleton until it lands) */}
      {gainable != null && gainable > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: SP.xs, padding: `9px ${SP.sm}`,
          background: withAlpha(C.success, 0.08), borderTop: BORDER, borderBottom: BORDER,
          margin: `${SP.xs} -${SP.sm}` }} data-testid="gainable-pts">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: FS['2xl'], fontWeight: 900, color: C.success, lineHeight: 1, ...NUM }}>+{gainable}</div>
            <div style={{ fontSize: FS['2xs'], color: C.neutral[500], textTransform: 'uppercase' }}>gainable pts</div>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', background: withAlpha(C.neutral[700], 0.5) }} />
          <div style={{ fontSize: FS.xs, color: C.neutral[300], lineHeight: 1.4 }}>
            Realistic ceiling via the moves below.
            {proj.current && proj.optimized &&
              ` You'd project ${proj.current.your_rank} → ${proj.optimized.your_rank}.`}
          </div>
        </div>
      )}

      {/* CATEGORY GAPS — fast (paints from /gaps immediately). DD-RO-UI-0727 #5: columns + cell count are
          FORMAT-DRIVEN from the league's scoring categories (meta.categories, the same source the desktop uses),
          NOT a hardcoded repeat(5)/slice(0,10) that dropped 2 of a 6x6's 12 categories. One row per side:
          columns = the larger side's category count; every category renders. */}
      <SectionHeader label="Category Gaps" color={PRIMARY} right={`rank / ${gaps?.total_teams || '—'}`} />
      {(() => {
        const meta = trades?.meta?.categories || gaps?.meta?.categories || null;
        const cols = Math.max((meta?.hitting || []).length, (meta?.pitching || []).length)
          || Math.ceil(catGaps.length / 2) || 5;
        return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 3 }} data-testid="gap-strip">
        {catGaps.map((g) => {
          const active = catFilter === g.category;
          return (
          <button key={g.category} type="button" data-testid="gap-box"
            aria-pressed={active} onClick={() => onGapTap && onGapTap(g.category)}
            style={{ background: withAlpha(tierColor(g.status), active ? 0.32 : 0.13), padding: `5px 2px`,
              textAlign: 'center', borderRadius: R.none, cursor: 'pointer', display: 'block',
              border: active ? `1px solid ${tierColor(g.status)}` : '1px solid transparent' }}>
            <div style={{ fontSize: FS['2xs'], color: C.neutral[400], textTransform: 'uppercase' }}>{g.category}</div>
            <div data-testid="gap-value" style={{ fontSize: FS.base, fontWeight: 900, color: tierColor(g.status), lineHeight: 1.1, ...NUM }}>{authStandings?.categories?.[g.category]?.rank ?? g.current_rank}</div>
            <div style={{ fontSize: FS['2xs'], color: C.neutral[500], ...NUM }}>
              {g.max_gainable_points > 0 ? `+${Math.round(g.max_gainable_points)}` : '—'}
            </div>
          </button>
          );
        })}
      </div>
        );
      })()}

      {/* projection coverage disclosure (frame 6 banner, inline) */}
      {coverage && coverage.missing_count > 0 && (
        <div style={{ display: 'flex', gap: SP.xs, alignItems: 'flex-start', padding: `9px ${SP.sm}`,
          borderTop: `1px solid ${C.warning}`, borderBottom: BORDER, background: withAlpha(C.warning, 0.07),
          margin: `${SP.xs} -${SP.sm}` }} data-testid="coverage-banner">
          <span style={{ color: C.warning, fontSize: FS.sm, flexShrink: 0 }}>⚠</span>
          <div style={{ fontSize: FS.xs, color: C.neutral[400], lineHeight: 1.45 }}>
            Projection coverage {Math.round(100 * coverage.with_projection / coverage.considered)}% —{' '}
            <span style={{ ...NUM }}>{coverage.missing_count}</span> of{' '}
            <span style={{ ...NUM }}>{coverage.considered}</span> players lack a projection; flagged{' '}
            <span style={{ border: BORDER, padding: '0 3px', fontSize: FS['2xs'], color: C.neutral[300] }}>NP</span>,
            never scored as valueless.
          </div>
        </div>
      )}

      {/* RECOMMENDED MOVES */}
      <SectionHeader label={catFilter ? `Recommended Moves · ${catFilter}` : 'Recommended Moves'}
        color={PRIMARY} right={catFilter ? 'tap the box again to clear' : 'by pts gain'} />
      {tradesLoading && !engineUpgrading ? (
        /* FRAME 2 — progressive: honest phase label + skeleton, never a blank spinner */
        <div data-testid="trades-loading">
          <div style={{ fontSize: FS.xs, color: C.neutral[400], marginBottom: SP.xs }}>
            Running trade simulations… <span style={{ color: C.neutral[500] }}>this can take a few seconds</span>
          </div>
          <div style={{ height: 5, background: C.neutral[800], borderRadius: R.none, overflow: 'hidden', marginBottom: SP.sm }}>
            <div style={{ height: '100%', width: '65%', background: PRIMARY }} />
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ height: 48, borderTop: BORDER, borderBottom: BORDER, marginBottom: 6,
              background: `linear-gradient(90deg, ${withAlpha(C.white, 0.02)}, ${withAlpha(C.white, 0.05)}, ${withAlpha(C.white, 0.02)})` }} />
          ))}
        </div>
      ) : engineUpgrading ? null : (
        <div data-testid="recommended-moves">
          {recs.slice(0, 3).map((r, i) => (
            <FaRow key={`fa${i}`} rec={r} onOpen={() => onOpen('fa', r)} />
          ))}
          {sequences.slice(0, 3).map((s, i) => (
            <TradeRow key={`tr${i}`} sequence={s} teamId={teamId} onOpen={() => onOpen('trade', s)} />
          ))}
          {(recs.length + sequences.length) > 0 && (
            <button type="button" onClick={onSeeAll} data-testid="see-all-trades"
              style={{ width: '100%', textAlign: 'center', padding: `${SP.xs} 0`, fontSize: FS.xs,
                fontWeight: 700, color: PRIMARY, borderTop: BORDER, borderBottom: BORDER,
                background: 'none', cursor: 'pointer' }}>
              See all {sequences.length} trade packages →
            </button>
          )}
          {(recs.length + sequences.length) === 0 && (
            <div style={{ padding: SP.md, textAlign: 'center', color: C.neutral[400], fontSize: FS.sm, borderTop: BORDER }}
                 data-testid="no-moves">
              No move improves your standing right now.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FaRow({ rec, onOpen }) {
  const name = rec?.player?.info?.full_name || 'Free agent';
  const drop = rec?.drop || {};
  const pts = rec?.score?.total_points_delta;
  return (
    <button type="button" onClick={onOpen} data-testid="fa-row"
      style={{ width: '100%', textAlign: 'left', borderTop: BORDER, borderBottom: BORDER, padding: `8px 0`,
        marginBottom: 6, background: 'none', cursor: 'pointer', display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={pillStyle(C.success, 0.16)}>Free Agent</span>
        <span style={{ marginLeft: 'auto', fontSize: FS.base, fontWeight: 900, color: C.success, ...NUM }}>
          {pts >= 0 ? `+${pts}` : pts}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: SP.xs }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: FS['2xs'], color: C.success, fontWeight: 800 }}>ADD</div>
          <div style={{ fontSize: FS.sm, fontWeight: 700, color: C.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        </div>
        <span style={{ color: C.neutral[600], fontSize: FS.sm }}>⇄</span>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
          <div style={{ fontSize: FS['2xs'], color: C.error, fontWeight: 800 }}>DROP</div>
          <div style={{ fontSize: FS.sm, fontWeight: 700, color: C.neutral[300], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{drop.player_name || '—'}</div>
        </div>
      </div>
    </button>
  );
}

function TradeRow({ sequence, teamId, onOpen }) {
  const pkg = sequence?.trade_package || {};
  const isThree = pkg.trade_type === 'three_team';
  const send = (pkg.user_sends?.players || [])[0]?.player_name || '—';
  const recv = (pkg.user_receives?.players || [])[0]?.player_name || '—';
  const net = pkg.net_value;
  const accept = pkg.acceptance_probability;
  const risk = sequence?.sensitivity?.risk_profile;
  return (
    <button type="button" onClick={onOpen} data-testid={isThree ? 'trade-row-3team' : 'trade-row'}
      style={{ width: '100%', textAlign: 'left', borderBottom: BORDER, padding: `8px 0`, marginBottom: 6,
        background: 'none', cursor: 'pointer', display: 'block',
        borderLeft: isThree ? `3px solid ${C.dataPurple}` : 'none', paddingLeft: isThree ? SP.xs : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {isThree
          ? <span style={pillStyle(C.dataPurple, 0.16)}>3-Team</span>
          : <span style={pillStyle(PRIMARY, 0.14)}>Trade</span>}
        <span style={{ marginLeft: 'auto', fontSize: FS.base, fontWeight: 900, color: net >= 0 ? C.success : C.error, ...NUM }}>
          {net >= 0 ? `+${net}` : net}
        </span>
      </div>
      <div style={{ fontSize: FS.sm, fontWeight: 700, color: C.white, lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {send} → {recv}{isThree ? ' (cycle)' : ''}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
        <span style={{ fontSize: FS.xs, fontWeight: 600, color: accept >= 0.5 ? C.success : C.warning, ...NUM }}>
          {accept != null ? `${Math.round(accept * 100)}%` : '—'} likely
        </span>
        {risk && <span style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>{sequence.sensitivity.risk_display_label || risk}</span>}
        <span style={{ marginLeft: 'auto', fontSize: FS.xs, fontWeight: 700, color: PRIMARY }}>View →</span>
      </div>
    </button>
  );
}
